import { Controller } from "@hotwired/stimulus"

type Point = [number, number]
type Room = { id: string; name: string; shape: SVGPolygonElement; points: Point[] }

const SNAP_CORNER_PX = 12
const SNAP_EDGE_PX = 8
const SLIVER = 0.1
const NESTED = 0.9

// Outline a room on a floor plan. Drag for a quick rectangle, or click each
// corner for any shape: click the first corner (or double-click) to close it.
// Backspace removes the last corner, Escape starts over. Points are kept as
// fractions of the image (0..1) so they survive re-uploading the plan.
//
// Corners snap to neighbouring rooms' corners and walls so shared walls line
// up, and an outline that partly overlaps another room is flagged before it
// can be saved (a room wholly inside another, like a closet, is fine). The
// server applies the same rule.
export default class extends Controller {
  static targets = ["canvas", "draft", "form", "room", "points", "save", "hint"]
  static values = { urlTemplate: String }

  declare readonly canvasTarget: HTMLElement
  declare readonly draftTarget: SVGSVGElement
  declare readonly formTarget: HTMLFormElement
  declare readonly roomTarget: HTMLSelectElement
  declare readonly pointsTarget: HTMLInputElement
  declare readonly saveTarget: HTMLButtonElement
  declare readonly hintTarget: HTMLElement
  declare readonly hasRoomTarget: boolean
  declare readonly urlTemplateValue: string

  private points: Point[] = []
  private others: Room[] = []
  private conflict = false
  private closed = false
  private dragStart: Point | null = null
  private dragging = false
  private pin: HTMLElement | null = null
  private pinMoved = false

  connect() {
    if (this.hasRoomTarget) this.selectRoom()
    this.onKey = this.onKey.bind(this)
    document.addEventListener("keydown", this.onKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
  }

  selectRoom() {
    const roomId = this.roomTarget.value
    if (roomId) this.formTarget.action = this.urlTemplateValue.replace("__ROOM__", roomId)

    this.canvasTarget.querySelectorAll<SVGElement>("[data-room-shape]").forEach((shape) => {
      shape.classList.toggle("is-current", shape.dataset.roomShape === roomId)
    })
    this.others = Array.from(this.canvasTarget.querySelectorAll<SVGPolygonElement>("[data-room-shape]"))
      .filter((shape) => shape.dataset.roomShape !== roomId)
      .map((shape) => ({
        id: shape.dataset.roomShape || "",
        name: shape.dataset.roomName || "another room",
        shape,
        points: (shape.getAttribute("points") || "").trim().split(/\s+/).map((pair) => {
          const [x, y] = pair.split(",").map(Number)
          return [x / 100, y / 100] as Point
        })
      }))
    this.clear()
  }

  pointerDown(event: PointerEvent) {
    // Grabbing a pin moves the pin instead of drawing.
    const pin = (event.target as HTMLElement).closest<HTMLElement>("[data-pin-url]")
    if (pin) {
      event.preventDefault()
      this.canvasTarget.setPointerCapture(event.pointerId)
      this.pin = pin
      this.pinMoved = false
      pin.dataset.originLeft ??= pin.style.left
      pin.dataset.originTop ??= pin.style.top
      return
    }

    if (!this.hasRoomTarget) return
    if (!this.roomTarget.value) {
      this.hintTarget.textContent = "Choose a room first, then outline it on the plan."
      return
    }
    event.preventDefault()
    this.canvasTarget.setPointerCapture(event.pointerId)
    this.dragStart = this.point(event)
    this.dragging = false
  }

  pointerMove(event: PointerEvent) {
    if (this.pin) {
      const [x, y] = this.rawPoint(event)
      this.pin.style.left = `${x * 100}%`
      this.pin.style.top = `${y * 100}%`
      this.pinMoved = true
      return
    }
    if (!this.dragStart) return
    const p = this.point(event)
    if (!this.dragging && Math.hypot(p[0] - this.dragStart[0], p[1] - this.dragStart[1]) > 0.015) this.dragging = true
    if (this.dragging) this.setShape(this.rectangle(this.dragStart, p), true)
  }

  pointerUp(event: PointerEvent) {
    if (this.pin) {
      this.canvasTarget.releasePointerCapture(event.pointerId)
      if (this.pinMoved) void this.savePin(this.pin, this.rawPoint(event))
      this.pin = null
      return
    }
    if (!this.dragStart) return
    this.canvasTarget.releasePointerCapture(event.pointerId)
    const p = this.point(event)

    if (this.dragging) {
      this.setShape(this.rectangle(this.dragStart, p), true)
    } else if (this.closed) {
      this.setShape([p], false)
    } else if (this.points.length >= 3 && this.nearFirst(p)) {
      this.setShape(this.points, true)
    } else {
      this.setShape([...this.points, p], false)
    }
    this.dragStart = null
    this.dragging = false
  }

  finish() {
    if (this.points.length >= 3) this.setShape(this.points, true)
  }

  undoPoint() {
    this.setShape(this.points.slice(0, -1), false)
  }

  clear() {
    this.setShape([], false)
  }

  private onKey(event: KeyboardEvent) {
    const target = event.target as HTMLElement
    if (target.closest("input, select, textarea")) return
    if (event.key === "Escape") this.clear()
    if (event.key === "Backspace" && this.points.length) {
      event.preventDefault()
      this.undoPoint()
    }
    if (event.key === "Enter" && this.points.length >= 3) {
      event.preventDefault()
      this.finish()
    }
  }

  private setShape(points: Point[], closed: boolean) {
    this.points = points
    this.closed = closed && points.length >= 3
    this.render()
    this.pointsTarget.value = this.points.length >= 3 ? JSON.stringify(this.points.map(([x, y]) => [+x.toFixed(5), +y.toFixed(5)])) : ""
    this.checkOverlaps()
    this.updateSave()
  }

  private render() {
    const svg = this.draftTarget
    const ns = "http://www.w3.org/2000/svg"
    svg.replaceChildren()
    if (!this.points.length) return

    const coords = this.points.map(([x, y]) => `${x * 100},${y * 100}`).join(" ")
    const shape = document.createElementNS(ns, this.closed ? "polygon" : "polyline")
    shape.setAttribute("points", coords)
    shape.setAttribute("class", this.closed ? "mapper-draft is-closed" : "mapper-draft")
    shape.setAttribute("vector-effect", "non-scaling-stroke")
    svg.appendChild(shape)

    this.points.forEach(([x, y], i) => {
      const dot = document.createElementNS(ns, "circle")
      dot.setAttribute("cx", String(x * 100))
      dot.setAttribute("cy", String(y * 100))
      dot.setAttribute("r", i === 0 && !this.closed && this.points.length >= 3 ? "1.4" : "0.8")
      dot.setAttribute("class", "mapper-corner")
      svg.appendChild(dot)
    })
  }

  // Flags rooms the outline partly overlaps, using the same rule as the server.
  private checkOverlaps() {
    const shape = this.points.length >= 3 ? this.points : null
    const clashes = shape ? this.others.filter((room) => this.relation(shape, room.points) === "overlapping") : []
    this.others.forEach((room) => room.shape.classList.toggle("is-conflict", clashes.includes(room)))
    this.conflict = clashes.length > 0
    if (this.conflict) {
      const names = clashes.map((room) => room.name).join(" and ")
      this.hintTarget.dataset.conflict = `This overlaps ${names}. Rooms can share walls or sit wholly inside another, but not half-overlap.`
    } else {
      delete this.hintTarget.dataset.conflict
    }
  }

  private updateSave() {
    const ready = Boolean(this.roomTarget.value) && this.points.length >= 3 && !this.conflict
    this.saveTarget.disabled = !ready
    this.hintTarget.classList.toggle("text-red-600", this.conflict)
    if (this.conflict && this.hintTarget.dataset.conflict) {
      this.hintTarget.textContent = this.hintTarget.dataset.conflict
      return
    }

    const room = this.roomTarget.selectedOptions[0]?.text.replace(/ ✓$/, "") ?? "the room"
    if (this.closed) {
      this.hintTarget.textContent = `Save this outline for ${room}, or click the plan to start again.`
    } else if (this.points.length >= 3) {
      this.hintTarget.textContent = "Keep clicking corners. Click the first corner, double-click or press Enter to finish."
    } else if (this.points.length) {
      this.hintTarget.textContent = "Click the next corner. Backspace removes the last one, Esc starts over."
    } else {
      this.hintTarget.textContent = `Drag a rectangle over ${room}, or click each corner for any shape.`
    }
  }

  private rectangle(a: Point, b: Point): Point[] {
    const [x1, x2] = [Math.min(a[0], b[0]), Math.max(a[0], b[0])]
    const [y1, y2] = [Math.min(a[1], b[1]), Math.max(a[1], b[1])]
    return [[x1, y1], [x2, y1], [x2, y2], [x1, y2]]
  }

  private nearFirst(p: Point) {
    const rect = this.canvasTarget.getBoundingClientRect()
    const [fx, fy] = this.points[0]
    return Math.hypot((p[0] - fx) * rect.width, (p[1] - fy) * rect.height) < 14
  }

  private point(event: PointerEvent): Point {
    return this.snap(this.rawPoint(event), this.canvasTarget.getBoundingClientRect())
  }

  private rawPoint(event: PointerEvent): Point {
    const rect = this.canvasTarget.getBoundingClientRect()
    const clamp = (v: number) => Math.min(1, Math.max(0, v))
    return [clamp((event.clientX - rect.left) / rect.width), clamp((event.clientY - rect.top) / rect.height)]
  }

  // Saves a dropped pin in the background so the plan doesn't reload. If the
  // server refuses the spot (outside the pin's room), the pin goes back.
  private async savePin(pin: HTMLElement, [x, y]: Point) {
    const token = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content || ""
    const body = new FormData()
    body.append("_method", "patch")
    body.append("x", x.toFixed(5))
    body.append("y", y.toFixed(5))

    try {
      const response = await fetch(pin.dataset.pinUrl || "", {
        method: "POST",
        body,
        headers: { Accept: "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || "Couldn't save that spot.")

      pin.dataset.originLeft = pin.style.left
      pin.dataset.originTop = pin.style.top
      pin.classList.add("ring-slate-dark")
      this.flash(result.message, false)
    } catch (error) {
      pin.style.left = pin.dataset.originLeft || pin.style.left
      pin.style.top = pin.dataset.originTop || pin.style.top
      this.flash((error as Error).message, true)
    }
  }

  private flash(message: string, isError: boolean) {
    this.hintTarget.textContent = message
    this.hintTarget.classList.toggle("text-red-600", isError)
    this.hintTarget.classList.toggle("text-emerald-700", !isError)
  }

  // Pulls a point onto a neighbouring room's corner, or failing that its wall,
  // when it's within a few pixels, so shared walls line up exactly.
  private snap(p: Point, rect: DOMRect): Point {
    const px = (a: Point, b: Point) => Math.hypot((a[0] - b[0]) * rect.width, (a[1] - b[1]) * rect.height)
    let best: Point | null = null
    let bestDistance = SNAP_CORNER_PX

    this.others.forEach((room) => room.points.forEach((corner) => {
      const d = px(p, corner)
      if (d < bestDistance) [best, bestDistance] = [corner, d]
    }))
    if (best) return best

    bestDistance = SNAP_EDGE_PX
    this.others.forEach((room) => room.points.forEach((a, i) => {
      const b = room.points[(i + 1) % room.points.length]
      const onEdge = this.project(p, a, b)
      const d = px(p, onEdge)
      if (d < bestDistance) [best, bestDistance] = [onEdge, d]
    }))
    return best ?? p
  }

  private project(p: Point, a: Point, b: Point): Point {
    const [dx, dy] = [b[0] - a[0], b[1] - a[1]]
    const length = dx * dx + dy * dy
    if (length === 0) return a
    const t = Math.min(1, Math.max(0, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length))
    return [a[0] + t * dx, a[1] + t * dy]
  }

  private relation(a: Point[], b: Point[]): "separate" | "nested" | "overlapping" {
    const shared = this.overlapArea(a, b)
    const [areaA, areaB] = [this.area(a), this.area(b)]
    if (shared <= SLIVER * Math.min(areaA, areaB)) return "separate"
    if (shared >= NESTED * areaA || shared >= NESTED * areaB) return "nested"
    return "overlapping"
  }

  private overlapArea(a: Point[], b: Point[], samples = 40): number {
    const xs = (s: Point[]) => s.map((p) => p[0])
    const ys = (s: Point[]) => s.map((p) => p[1])
    const x0 = Math.max(Math.min(...xs(a)), Math.min(...xs(b)))
    const x1 = Math.min(Math.max(...xs(a)), Math.max(...xs(b)))
    const y0 = Math.max(Math.min(...ys(a)), Math.min(...ys(b)))
    const y1 = Math.min(Math.max(...ys(a)), Math.max(...ys(b)))
    if (x1 <= x0 || y1 <= y0) return 0

    const [w, h] = [(x1 - x0) / samples, (y1 - y0) / samples]
    let hits = 0
    for (let i = 0; i < samples; i++) {
      for (let j = 0; j < samples; j++) {
        const p: Point = [x0 + (i + 0.5) * w, y0 + (j + 0.5) * h]
        if (this.inside(p, a) && this.inside(p, b)) hits++
      }
    }
    return hits * w * h
  }

  private area(s: Point[]): number {
    return Math.abs(s.reduce((sum, [x1, y1], i) => {
      const [x0, y0] = s[(i + s.length - 1) % s.length]
      return sum + x0 * y1 - x1 * y0
    }, 0)) / 2
  }

  private inside([x, y]: Point, s: Point[]): boolean {
    let inside = false
    for (let i = 0, j = s.length - 1; i < s.length; j = i++) {
      const [xi, yi] = s[i]
      const [xj, yj] = s[j]
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
    }
    return inside
  }
}
